import React, { useEffect, useRef, useState } from 'react';
import { photoCameraError } from './photoCamera';
import { hasNativePhotoCamera, listPhotoCameras } from './nativePhotoCamera';
import './PhotoCameraToolbar.css';

export default function PhotoCameraToolbar({ camera, onChange, storageWarning }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const close = () => { setOpen(false); trigger.current?.focus(); };
  return (
    <div className="photo-camera-control" onKeyDown={event => {
      if (event.key === 'Escape' && open) { event.stopPropagation(); close(); }
    }}>
      <button ref={trigger} type="button" className="photo-camera-trigger" aria-label="카메라 선택 열기" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(value => !value)}>CAM</button>
      {open && <PhotoCameraPanel camera={camera} onChange={onChange} storageWarning={storageWarning} onClose={close} />}
    </div>
  );
}

function PhotoCameraPanel({ camera, onChange, storageWarning, onClose }) {
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const alive = useRef(false);
  const request = useRef(0);
  const listRequest = useRef(0);
  const timer = useRef(null);
  useEffect(() => {
    alive.current = true;
    const media = navigator.mediaDevices;
    const refresh = async () => {
      const current = ++listRequest.current;
      try {
        const list = await listPhotoCameras();
        if (alive.current && current === listRequest.current) setDevices((list || []).filter(d => d.kind === 'videoinput'));
      } catch { /* 권한 확인 버튼에서 구체적인 오류를 안내한다. */ }
    };
    refresh();
    media?.addEventListener?.('devicechange', refresh);
    return () => { alive.current = false; request.current += 1; listRequest.current += 1; clearTimeout(timer.current); media?.removeEventListener?.('devicechange', refresh); };
  }, []);

  const discover = async () => {
    const current = ++request.current;
    setBusy(true);
    setMessage('카메라 권한을 확인하고 있습니다.');
    const timeout = setTimeout(() => {
      if (!alive.current || current !== request.current) return;
      request.current += 1;
      setBusy(false);
      setMessage('권한 응답이 지연되고 있습니다. 브라우저의 카메라 권한을 확인해주세요.');
    }, 15000);
    timer.current = timeout;
    try {
      if (!hasNativePhotoCamera()) {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('NO_MEDIA_DEVICES');
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        stream.getTracks().forEach(t => t.stop());
      }
      if (!alive.current || current !== request.current) return;
      const list = await listPhotoCameras();
      if (!alive.current || current !== request.current) return;
      setDevices(list.filter(d => d.kind === 'videoinput'));
      setMessage('목록에서 연결한 USB 카메라를 선택해주세요.');
    } catch (error) {
      if (alive.current && current === request.current) setMessage(photoCameraError(error));
    } finally {
      clearTimeout(timeout);
      if (alive.current && current === request.current) setBusy(false);
    }
  };
  const missing = camera.mode === 'usb' && camera.deviceId && !devices.some(d => d.deviceId === camera.deviceId);
  return (
    <section className="photo-camera-toolbar" role="dialog" aria-label="카메라 선택">
      <div className="photo-camera-heading"><span>CAM</span><strong>카메라 선택</strong><button type="button" className="photo-camera-close" aria-label="카메라 선택 닫기" autoFocus onClick={onClose}>×</button></div>
      <div className="photo-camera-actions">
        <button type="button" aria-pressed={camera.mode === 'rear'} disabled={busy} onClick={() => onChange({ mode: 'rear', deviceId: '' })}>후면 카메라</button>
        <button type="button" aria-pressed={camera.mode === 'usb'} disabled={busy} onClick={() => onChange({ mode: 'usb', deviceId: camera.deviceId || '' })}>USB 카메라</button>
      </div>
      {camera.mode === 'usb' && <>
        <select aria-label="USB 촬영 장치" value={camera.deviceId} disabled={busy} onChange={e => onChange({ mode: 'usb', deviceId: e.target.value })}>
          <option value="">USB 장치 선택</option>
          {missing && <option value={camera.deviceId}>이전 선택 장치 · 연결/권한 확인 필요</option>}
          {devices.filter(d => d.deviceId).map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `카메라 ${i + 1} · 권한 확인 필요`}</option>)}
        </select>
        <button type="button" disabled={busy} onClick={discover}>{busy ? '확인 중…' : '카메라 권한 / 목록 새로고침'}</button>
      </>}
      {message && <p role="status">{message}</p>}
      {storageWarning && <p role="status">설정을 저장할 수 없어 이 화면에서만 적용됩니다.</p>}
    </section>
  );
}
